import { Injectable } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { HttpClient } from "@angular/common/http";
import { Observable, BehaviorSubject, of, throwError } from "rxjs";
import {
  switchMap,
  filter,
  take,
  catchError,
  shareReplay,
  timeout,
  map,
  retry,
} from "rxjs/operators";
import { SESSION_STORAGE_KEYS } from "../../../constants";
import { TokenPropertyService } from '../token-property.service';

@Injectable({
  providedIn: "root",
})
export class QuicksightService {
  private token: any;
  private idToken: any;
  private cachedClientCode: string | null = null;
  private debugMode = !environment.production; // Only debug in non-production environments
  private registrationInProgress$: Observable<any> | null = null;
  private registrationCompleted$ = new BehaviorSubject<boolean>(false);
  private isRegistrationNeeded = false; // Flag to track if registration was requested

  constructor(private http: HttpClient, private tokenPropertyService: TokenPropertyService) {
    this.refreshTokens();
  }

  /**
   * Refreshes the tokens from sessionStorage
   * Can be called before embedding to ensure we have the latest token
   * @returns true if tokens were successfully loaded, false otherwise
   */
  refreshTokens(): boolean {
    const oldToken = this.idToken;
    this.token = sessionStorage.getItem("token");
    this.idToken = sessionStorage.getItem("id_token");

    // Clear the cache when refreshing tokens to ensure fresh client code extraction
    const hadCachedCode = this.cachedClientCode !== null;
    this.cachedClientCode = null;

    if (!environment.production) {
      this.logInfo("Tokens refreshed:", {
        hadCachedCode,
        tokenChanged: oldToken !== this.idToken,
        hasNewIdToken: !!this.idToken,
        hasNewToken: !!this.token,
      });
    }

    // Validate that we have at least one usable token
    const hasValidToken =
      this.validateToken(this.idToken) || this.validateToken(this.token);

    if (!hasValidToken) {
      this.logWarn("No valid tokens found after refresh");
    } else {
      this.logInfo("Tokens refreshed successfully");
    }

    return hasValidToken;
  }

  /**
   * Validates that a token is properly formatted
   * @param token The token to validate
   * @returns true if token is valid, false otherwise
   */
  private validateToken(token: string | null): boolean {
    if (!token) return false;

    const tokenParts = token.split(".");
    if (tokenParts.length !== 3) return false;

    try {
      // Use TokenPropertyService to validate and parse
      const payload = this.tokenPropertyService.getAllProperties(token.includes('id_token') ? 'id_token' : 'token');
      return !!payload;
    } catch (e: any) {
      this.logError("Error validating token format", e);
      return false;
    }
  }

  /**
   * Enable or disable debug mode for more detailed logging
   */
  setDebugMode(enable: boolean): void {
    this.debugMode = enable;
  }

  /**
   * Gets the client code from the token or session information
   * This method extracts the client code from the token stored in local storage
   * Prioritizes id_token over regular token
   *
   * @returns The client code from the token or null if not available
   */
  getClientCode(): string | null {
    // Return cached value if available (only cache successful results, not null)
    if (this.cachedClientCode !== null) {
      return this.cachedClientCode;
    }

    try {
      // Refresh tokens to ensure we have the latest
      const hasValidTokens = this.refreshTokens();
      if (!hasValidTokens) {
        this.logWarn("No valid tokens available to extract client code");
        return null;
      }

      // First check id_token as it has priority
      if (this.idToken) {
        const clientCode = this.tokenPropertyService.getPropertyFromToken('custom:clientcode', 'id_token');
        const customUserId = this.tokenPropertyService.getPropertyFromToken('custom:userid', 'id_token');
        const payload = this.tokenPropertyService.getAllProperties('id_token');
        
        // Log the full payload for debugging (be careful in production)
        if (!environment.production && payload) {
          this.logInfo("Full id_token payload for debugging:", {
            allKeys: Object.keys(payload),
            customClientCode: payload["custom:clientcode"],
            customUserId: customUserId,
            payloadSubset: Object.keys(payload).reduce((acc, key) => {
              if (key.includes("client") || key.includes("code") || key.includes("custom")) {
                acc[key] = payload[key];
              }
              return acc;
            }, {} as any),
          });
        }
        
        // Use only custom:clientcode as agreed
        if (payload && payload["custom:clientcode"]) {
          this.cachedClientCode = payload["custom:clientcode"];
          this.logInfo(
            "Client code extracted from id_token:",
            this.cachedClientCode
          );
          return this.cachedClientCode;
        } else {
          this.logWarn(
            "custom:clientcode attribute not found in id_token payload",
            payload ? {
              payloadKeys: Object.keys(payload).filter(
                (k) => k.includes("client") || k.includes("code")
              ),
            } : {}
          );
        }
      } else {
        this.logWarn("No id_token available");
      }

      // Fallback to regular token if no client code found in ID token
      if (this.token) {
        const clientCode = this.tokenPropertyService.getPropertyFromToken('custom:clientcode', 'token');
        
        if (clientCode) {
          this.cachedClientCode = clientCode;
          this.logInfo("Client code extracted from token fallback:", this.cachedClientCode);
          return this.cachedClientCode;
        } else {
          this.logWarn("custom:clientcode attribute not found in token payload");
        }
      } else {
        this.logWarn("No regular token available");
      }

      // Don't cache null results - let future calls retry when tokens are available
      this.logWarn("Failed to extract client code from any available token");
      return null;
    } catch (error) {
      this.logError("Error extracting client code from token", error);
      return null;
    }
  }

  /**
   * Force client code to always be a specific value (useful for testing or fixing inconsistencies)
   * @param clientCode The client code to force
   */
  forceClientCode(clientCode: string | null): void {
    this.cachedClientCode = clientCode;
  }

  /**
   * Prepares the configuration object for QuickSight embedding by adding the pClientId parameter
   *
   * @param config The original embedding configuration
   * @returns An Observable of the configuration with the pClientId parameter added
   */
  addClientIdToEmbedConfig(config: any): Observable<any> {
    if (!config) {
      return of(config);
    }

    // Create a deep copy of the config
    const updatedConfig = JSON.parse(JSON.stringify(config));
    const clientCode = this.getClientCode();

    // If clientCode is available, add it to the configuration
    if (clientCode) {
      // Initialize parameters array if it doesn't exist
      if (!updatedConfig.parameters) {
        updatedConfig.parameters = [];
      }

      // Add or update the pClientId parameter
      const existingParamIndex = updatedConfig.parameters.findIndex(
        (param: any) => param.Name === "pClientId"
      );

      if (existingParamIndex >= 0) {
        // Update existing parameter
        updatedConfig.parameters[existingParamIndex].Values = [clientCode];
      } else {
        // Add new parameter
        updatedConfig.parameters.push({
          Name: "pClientId",
          Values: [clientCode],
        });
      }

      // Ensure the parameters are explicitly passed as URL parameters
      if (!updatedConfig.parameterPassingMethod) {
        updatedConfig.parameterPassingMethod = "URL";
      }

      this.logInfo("QuickSight embedding configuration:", {
        clientCode,
        hasClientId: !!clientCode,
        parameterPassingMethod: updatedConfig.parameterPassingMethod,
        parameters: updatedConfig.parameters,
      });

      return of(updatedConfig);
    }

    // Log when client code is not available
    this.logWarn("Client code not available for QuickSight embedding");
    return of(config);
  }

  /**
   * Prints the current state of QuickSight embedding configuration to the console
   * This can be useful for debugging embedding issues
   */
  debugEmbeddingStatus(): void {
    // Only run in non-production mode
    if (environment.production) return;

    const clientCode = this.getClientCode();

    this.logInfo("QuickSight Embedding Status:", {
      hasIdToken: !!this.idToken,
      hasToken: !!this.token,
      hasClientCode: !!clientCode,
      clientCode: clientCode,
      idTokenFormat: this.idToken
        ? this.formatTokenSummary(this.idToken)
        : "Not available",
      tokenFormat: this.token
        ? this.formatTokenSummary(this.token)
        : "Not available",
    });
  }

  /**
   * Formats a token summary for debugging (safely shows structure without leaking sensitive data)
   */
  private formatTokenSummary(token: string): any {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) return { valid: false, parts: parts.length };

      // Determine token type and use service
      const tokenType = sessionStorage.getItem('id_token') === token ? 'id_token' : 'token';
      const payload = this.tokenPropertyService.getAllProperties(tokenType);

      if (!payload) return { valid: false, error: 'Could not parse token' };

      return {
        valid: true,
        length: token.length,
        hasClientCode: !!payload["custom:clientcode"],
        relevantFields: Object.keys(payload).filter(
          (k) => k.includes("client") || k.includes("code")
        ),
      };
    } catch (e: any) {
      return { valid: false, error: e.message };
    }
  }

  /**
   * Checks for potential embedding configuration inconsistencies
   * @param config The embedding configuration to check
   * @param componentName The name of the component using the configuration (for logging)
   */
  checkEmbeddingConsistency(config: any, componentName: string): void {
    // Only run in non-production mode
    if (environment.production) return;

    if (!config) {
      this.logWarn(`${componentName}: No config provided`);
      return;
    }

    const issues: string[] = [];

    // Check for parameterPassingMethod
    if (!config.parameterPassingMethod) {
      issues.push("parameterPassingMethod is missing");
    } else if (config.parameterPassingMethod !== "URL") {
      issues.push(
        `parameterPassingMethod is set to '${config.parameterPassingMethod}' instead of 'URL'`
      );
    }

    // Check for parameters array
    if (!config.parameters || !Array.isArray(config.parameters)) {
      issues.push("parameters is missing or not an array");
    } else {
      // Check for pClientId parameter
      const pClientIdParam = config.parameters.find(
        (p: any) => p.Name === "pClientId"
      );

      if (!pClientIdParam) {
        issues.push("pClientId parameter is missing");
      } else if (!pClientIdParam.Values || !pClientIdParam.Values.length) {
        issues.push("pClientId parameter has no values");
      } else if (!pClientIdParam.Values[0]) {
        issues.push("pClientId parameter has empty value");
      }
    }

    // Log results
    if (issues.length > 0) {
      this.logWarn(
        `${componentName} has embedding configuration issues:`,
        issues,
        config
      );
    } else {
      this.logInfo(`${componentName} embedding configuration is valid`);
    }
  }

  /**
   * Debug helper for development only
   * This method combines all debugging functionality in one place
   * Will be completely ignored in production builds
   */
  debugAll(): void {
    if (environment.production) {
      // Do nothing in production
      return;
    }

    // Show current debugging status
    this.logInfo("QuickSight debug helper activated");
    this.logInfo("Environment:", {
      production: environment.production,
      debugModeEnabled: this.debugMode,
    });

    // Refresh tokens and show their status
    this.refreshTokens();

    // Show embedding status
    this.debugEmbeddingStatus();

    // Check client code
    const clientCode = this.getClientCode();
    if (clientCode) {
      this.logInfo("Client code is available:", clientCode);
    } else {
      this.logWarn("Client code is not available");
    }
  }

  /**
   * Check if the user has QuickSight access by making a POST request to the backend
   * This should be called when setting tokens to ensure QuickSight access is checked on each login
   *
   * @returns An observable that completes when the QuickSight access check is done
   */
  checkQuicksightAccess(): Observable<any> {
    this.logInfo("Checking QuickSight access");

    // Get the tokens from sessionStorage (tab-isolated)
    const token = sessionStorage.getItem("token");
    const idToken = sessionStorage.getItem("id_token");

    if (!token || !idToken) {
      return throwError(() => new Error("Missing authentication tokens"));
    }

    if (sessionStorage.getItem(SESSION_STORAGE_KEYS.QUICKSIGHT_REGISTERED_KEY) === 'true') {
      return of({ message: "QuickSight already registered, skipping check" });
    }

    return this.http
      .post<any>(
        `${environment.API_URL}/user/quicksight/registration`,
        {}
      )
      .pipe(
        timeout(15000),
        retry(1), // Add retry logic
        catchError((err) => {
          this.logError("QuickSight registration failed", err);
          throw err;
        }),
        map((response) => {
          this.logInfo("QuickSight registration completed successfully");
          this.registrationCompleted$.next(true);
          return response;
        }),
        shareReplay(1) // Share the result with multiple subscribers
      );
  }

  /**
   * Ensures the user is registered with QuickSight before proceeding.
   * Returns an observable that completes when registration is done.
   * Only blocks if registration was explicitly requested (usually on first login).
   * On subsequent page loads/refreshes, it won't block if registration was previously completed.
   */
  ensureRegistered(): Observable<any> {
    // If registration was previously completed successfully, don't block
    if (this.registrationCompleted$.value) {
      this.logInfo("Registration previously completed, skipping");
      return of(true);
    }

    // If registration wasn't requested (not first login), don't block
    if (!this.isRegistrationNeeded) {
      this.logInfo("Registration not requested, proceeding without waiting");
      return of(true);
    }

    // If registration is in progress, wait for it, but add a timeout to avoid hanging
    if (this.registrationInProgress$) {
      // Reset the registration in progress if it's been more than 30 seconds
      // This prevents a stale observable from blocking new attempts
      setTimeout(() => {
        if (this.registrationInProgress$ && !this.registrationCompleted$.value) {
          this.logWarn("Registration appears to be hanging, will reset on next call");
          this.registrationInProgress$ = null;
        }
      }, 30000); // 30 second timeout
      return this.registrationInProgress$;
    }

    // Create the registration observable
    const registration$ = this.checkQuicksightAccess().pipe(
      catchError((err) => {
        this.logError("QuickSight registration failed", err);
        this.registrationCompleted$.next(false);
        this.isRegistrationNeeded = false; // Reset flag on error
        this.registrationInProgress$ = null; // Clear the in-progress flag on error
        throw err;
      }),
      switchMap((response) => {
        this.registrationCompleted$.next(true);
        this.isRegistrationNeeded = false; // Reset flag after successful completion

        // Clear the in-progress Observable after a delay to allow future attempts
        setTimeout(() => {
          this.registrationInProgress$ = null;
        }, 1000);

        return of(true);
      }),
      shareReplay(1) // Share the result with all subscribers
    );

    // Assign to the class variable for tracking
    this.registrationInProgress$ = registration$;

    // Important: Force immediate subscription to ensure the HTTP request is actually sent
    registration$.subscribe(
      () => this.logInfo("QuickSight registration completed"),
      (error) => this.logError("QuickSight registration failed", error)
    );

    return this.registrationInProgress$;
  }

  /**
   * Explicitly triggers QuickSight registration and waits for it to complete.
   * This should be called during the login flow.
   *
   * @returns An Observable that completes when registration is done
   */
  registerQuicksight(): Observable<any> {
    this.logInfo("Explicitly triggering QuickSight registration");
    this.isRegistrationNeeded = true;

    // Reset registration status if needed
    if (this.registrationInProgress$ === null) {
      this.registrationCompleted$.next(false);
    }

    // This will initiate a new registration if one isn't already in progress
    return this.ensureRegistered();
  }

  /**
   * Log helper methods that only log in non-production environments
   */
  private logInfo(message: string, ...data: any[]): void {
    if (!environment.production) {
      console.log(`[QuicksightService] ${message}`, ...data);
    }
  }

  private logWarn(message: string, ...data: any[]): void {
    if (!environment.production) {
      console.warn(`[QuicksightService] ${message}`, ...data);
    }
  }

  private logError(message: string, ...data: any[]): void {
    // We always log errors, even in production, but with less detail
    if (environment.production) {
      console.error(`[QuicksightService] ${message}`);
    } else {
      console.error(`[QuicksightService] ${message}`, ...data);
    }
  }

  /**
   * Debug method to directly test the QuickSight registration API
   * Call this method with .subscribe() to force a registration attempt
   */
  debugTestRegistrationAPI(): Observable<any> {
    this.logInfo("Directly testing QuickSight registration API");

    return this.checkQuicksightAccess().pipe(
      catchError((err) => {
        this.logError("API test failed", err);
        return of({ success: false, error: err });
      }),
      map((result) => ({ success: true, result }))
    );
  }
}